/**
 * AMIS AutoSales License Notification 2.0.6 — adapter
 * PLF giữ quyền quyết định nội dung, thời điểm hiển thị và link Store.
 * File này chỉ lo load script, map dữ liệu mTax → PLF, mount đúng 1 lần, cleanup lifecycle.
 * Tham khảo: documents/misa-autosales-license-notification-2.0.6.md
 */
import dateUtil, { FORMAT_DD_MM_YYYY } from '@/utils/dateUtil';
import CommonFunction from '@/common/commonFunction';
import EventBusGlobal, { GlobalEventName } from '@/sys/eventBusGlobal';
var _handler = null;
var _listeners = null;
var _activeTaxCode = null;
var _bannerElement = null;
var _bannerNotiElement = null;
var _bannerListenerRegistered = false;
var _slotObserver = null;
var _slotMutationObserver = null;
var _fullscreenAlertRoot = null;
var _fullscreenAlertMountTimer = null;
var _licenseNotificationGeneration = 0;
function getConfig() {
	return (window.__env && window.__env.alnConfig) || {};
}
function isEnabled() {
	var config = getConfig();
	return (
		window.__env.siteConfig &&
		window.__env.siteConfig.isAmisApp &&
		config.enabled &&
		config.libUrl
	);
}
function loadScript(url) {
	return new Promise(function (resolve, reject) {
		var existing = document.querySelector('script[src="' + url + '"]');
		if (existing) {
			resolve();
			return;
		}
		var script = document.createElement('script');
		script.src = url;
		script.type = 'text/javascript';
		script.onload = function () {
			resolve();
		};
		script.onerror = function () {
			reject(new Error('Failed to load ALN library'));
		};
		document.body.appendChild(script);
	});
}
/**
 * Map LicenseInfo entity của mTax sang licenseInfo.original PLF
 * PLF chỉ cần StartDate/EndDate để áp dụng timing; realUsage/lastBought để trống an toàn.
 */
function mapLicenseInfo(licenseInfo) {
	return {
		original: {
			StartDate: dateUtil.parseDateTimeToString(
				dateUtil.parseJsonToDate(licenseInfo.StartDate),
				FORMAT_DD_MM_YYYY
			),
			EndDate: dateUtil.parseDateTimeToString(
				dateUtil.parseJsonToDate(licenseInfo.ExpiredDate),
				FORMAT_DD_MM_YYYY
			),
			LicenseType:
				licenseInfo.LicenseType !== undefined
					? licenseInfo.LicenseType
					: 0,
			IsTrial: licenseInfo.IsTrial === true,
			IsFree: false, // AMIS không trả IsFree — backend không cấp, giữ mặc định
		},
		realUsage: {},
		lastBought: {},
		branchInfos: [],
	};
}
function buildAppInfo(options, config) {
	return {
		productCode: window.__appInfo.ApplicationCode,
		productName: window.__appInfo.ApplicationName,
		appDomain: window.__appInfo.ApplicationURL,
		taxCode: options.taxCode,
		misaId: options.misaId,
		userId: options.userId,
		employeeCode: options.employeeCode,
		token: options.token,
		clientid: options.clientid,
		lang: options.lang,
	};
}
/**
 * MISA Notification In-App v2 (slot #misa-notif-top-slot) có notification thực sự đang hiển thị không.
 * Không dùng kích thước slot hoặc root Vue vì các root có thể tồn tại dù chỉ chứa <!--v-if-->.
 */
function isMisaNotifShowing() {
	var slot = document.getElementById('misa-notif-top-slot');
	if (!slot) return false;
	//Lấy ra tất cả các thẻ con của thẻ misa-notif-top-slot và kiểm tra các thẻ con có giá trị chiều vào hoặc chiều rộng khác 0 hay không
	var elements = slot.querySelectorAll('*');
	for (var i = 0; i < elements.length; i++) {
		var element = elements[i];
		if (!element.textContent || !element.textContent.trim()) continue;
		var opacity = 1;
		var current = element;
		var isHidden = false;
		
		while (current && current !== slot.parentNode) {
			var style = window.getComputedStyle(current);
			if (
				style.display === 'none' ||
				style.visibility === 'hidden' ||
				style.visibility === 'collapse' ||
				current.getAttribute('aria-hidden') === 'true'
			) {
				isHidden = true;
				break;
			}
			opacity *= Number(style.opacity);
			if (current === slot) break;
			current = current.parentElement;
		}
		if (isHidden || opacity === 0) continue;
		var rect = element.getBoundingClientRect();
		if (rect.width > 0 && rect.height > 0) return true;
	}
	return false;
}
/**
 * Ẩn/hiện banner license theo trạng thái MISA notif.
 * display:none → ResizeObserver tự đo height=0 → --banner-height về 0px, không cần dispose.
 */
function onMisaNotifVisible(visible) {
	if (_bannerElement) {
		me.attachSlotObserver();
	}
}
/**
 * Đồng bộ hiển thị banner theo DOM của slot MISA notif — nguồn sự thật duy nhất,
 * không phụ thuộc callback/sự kiện của lib (onMessage không đáng tin).
 */
function syncBannerVisibility() {
	if (!_bannerElement) return;
	let text = isMisaNotifShowing() ? 'none' : '';
	_bannerElement.style.display = text;
}
function attachSlotObserver() {
	var slot = document.getElementById('misa-notif-top-slot');
	if (!slot) return;
	if (!_slotObserver && typeof ResizeObserver !== 'undefined') {
		_slotObserver = new ResizeObserver(function () {
			syncBannerVisibility();
		});
		_slotObserver.observe(slot);
	}
	if (!_slotMutationObserver && typeof MutationObserver !== 'undefined') {
		_slotMutationObserver = new MutationObserver(function () {
			syncBannerVisibility();
		});
		_slotMutationObserver.observe(slot, {
			attributes: true,
			attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
			characterData: true,
			childList: true,
			subtree: true,
		});
	}
}
function detachSlotObserver() {
	if (_slotObserver) {
		_slotObserver.disconnect();
		_slotObserver = null;
	}
	if (_slotMutationObserver) {
		_slotMutationObserver.disconnect();
		_slotMutationObserver = null;
	}
}
/**
 * Xóa element banner license khỏi #bannerHeader (Main.vue).
 * Idempotent — gọi nhiều lần an toàn. ResizeObserver của observeElementHeight
 * tự đo lại height = 0 → --banner-height về 0px, không cần dispose.
 */
export function clearBannerHeader() {
	if (_bannerNotiElement && _bannerNotiElement.parentNode) {
		_bannerNotiElement.parentNode.removeChild(_bannerNotiElement);
	}
	_bannerNotiElement = null;
	_bannerElement = null;
}

/**
 * Khởi tạo PLF handler đúng 1 lần cho context (tax code).
 * @returns {Promise<Object|null>} handler nếu PLF sẵn sàng; null nếu thiếu config/lib/data hoặc lỗi.
 */
export async function initLicenseNotification(options) {
	if (!isEnabled() || !options) return null;
	var config = getConfig();
	var taxCode = options.taxCode || '';
	// Đổi MST thì hủy instance cũ trước khi tạo mới; không phụ thuộc ngày trên client.
	if (_handler && _activeTaxCode !== taxCode) {
		destroyLicenseNotification();
	}
	// Đã mount cho đúng context → trả handler hiện tại, không tạo trùng
	if (_handler) return _handler;
	if (!window.MISALicenseNotification) {
		try {
			await loadScript(config.libUrl);
		} catch (err) {
			// eslint-disable-next-line no-console
			console.warn('[LicenseNotification] load failed');
			return null;
		}
	}
	if (
		!window.MISALicenseNotification ||
		!window.MISALicenseNotification.getInstance
	) {
		// eslint-disable-next-line no-console
		console.warn(
			'[LicenseNotification] MISALicenseNotification unavailable'
		);
		return null;
	}
	var appInfo = buildAppInfo(options, config);
	if (!appInfo.taxCode) return null;
	let licenseInfo = mapLicenseInfo(options.licenseInfo);
	var generation = ++_licenseNotificationGeneration;
	_handler = window.MISALicenseNotification.getInstance(appInfo, licenseInfo);
	_activeTaxCode = taxCode;
	_listeners = {
		onElementCreated: function (element, notiInfos, type) {
			if (options.onElementCreated)
				options.onElementCreated(element, notiInfos, type);
		},
		onElementRendered: function (element, notiInfos, type) {
			if (options.onElementRendered)
				options.onElementRendered(element, notiInfos, type);
		},
		onHidden: function (triggeredClassName) {
			if (generation !== _licenseNotificationGeneration) return;
			if (options.onHidden) options.onHidden(triggeredClassName);
			clearBannerHeader();
		},
		afterRedirectClicked: function (event, notiInfos) {
			if (options.onRedirectClicked)
				options.onRedirectClicked(event, notiInfos);
		},
		afterPostponeClicked: function (event, notiInfos) {
			if (options.afterPostponeClicked)
				options.afterPostponeClicked(event, notiInfos);
		},
	};
	if (_handler.addEventListeners) _handler.addEventListeners(_listeners);
	try {
		if (_handler.initialize) {
			let notification = await _handler.initialize({});
			if (
				notification &&
				notification.isDisplayed &&
				notification.element
			) {
				switch (notification.type) {
					case MISALicenseNotification.LICENSE_NOTIFICATION_TYPE
						.HEADER:
						setTimeout(() => {
							// destroy đã gọi trước khi timeout cháy → không append nữa
							if (!_handler) return;
							let bannerElement =
								document.querySelector('#bannerHeader');
							if (bannerElement) {
								// MISA notif có thể show TRƯỚC khi banner append (App.vue mount sớm hơn Main.vue,
								// event emit trước khi listener đăng ký) → ẩn ban đầu nếu DOM slot đang có nội dung
								bannerElement.style.display =
									isMisaNotifShowing() ? 'none' : 'block';
								bannerElement.appendChild(notification.element);
								// Lưu element PLF đã append để clear khỏi DOM sau này
								_bannerNotiElement = notification.element;
								// Lưu banner để ẩn/hiện theo MISA notif (ưu tiên MISA notif)
								_bannerElement = bannerElement;
								// Chỉ đăng ký 1 lần — tránh trùng listener nếu init lại
								if (!_bannerListenerRegistered) {
									EventBusGlobal.$on(
										GlobalEventName.misaNotifVisible,
										onMisaNotifVisible
									);
									_bannerListenerRegistered = true;
								}
								// Theo dõi slot MISA notif: slot chiếm layout → ẩn banner,
								// slot về 0 → hiện lại (DOM là nguồn sự thật, không dựa event lib)
								attachSlotObserver();
								// Theo dõi chiều cao banner: set --banner-height lên :root
								// để sidebar/navbar/router-view tính lại vị trí & chiều cao
								CommonFunction.observeElementHeight(
									bannerElement,
									'--banner-height'
								);
							}
						}, 100);
						break;
					case MISALicenseNotification.LICENSE_NOTIFICATION_TYPE
						.FULLSCREEN_ALERT:
						break;
				}
			}
		}
	} catch (err) {
		// eslint-disable-next-line no-console
		console.warn('[LicenseNotification] initialize failed');
		destroyLicenseNotification();
		return null;
	}
	return _handler;
}
function reinitialize() {
	if (!_handler || !_handler.initialize) return;
	try {
		_handler.initialize({}, true, true);
	} catch (err) {
		// eslint-disable-next-line no-console
		console.warn('[LicenseNotification] re-initialize failed');
	}
}
/**
 * Hủy PLF handler, dọn listener — gọi khi logout/session timeout/đổi MST.
 */
export function destroyLicenseNotification() {
	if (_handler && _handler.removeEventListeners && _listeners) {
		try {
			_handler.removeEventListeners(_listeners);
		} catch (err) {
			// silent — không crash khi logout
		}
	}
	if (_handler && _handler.destroy) {
		try {
			_handler.destroy();
		} catch (err) {
			// silent
		}
	}
	_licenseNotificationGeneration++;
	_handler = null;
	_listeners = null;
	_activeTaxCode = null;
	detachSlotObserver();
	// Gỡ listener MISA notif + reset banner license
	if (_bannerListenerRegistered) {
		EventBusGlobal.$off(
			GlobalEventName.misaNotifVisible,
			onMisaNotifVisible
		);
		_bannerListenerRegistered = false;
	}
	clearBannerHeader();
}
